import { z } from 'zod';
import type { PapelDaMaquina } from '../sessoes/modos';

/**
 * PROJETO = (máquina, cwd normalizado) — a unidade de visibilidade do ADR-0020.
 *
 * ## Por que normalizar, e por que a normalização depende da MÁQUINA
 *
 * Medido na produção em 2026-09-21: 192 sessões, 45 pares distintos
 * (máquina, cwd) — **42** se o cwd for comparado sem diferenciar caixa. A
 * diferença são três projetos PARTIDOS pela caixa da letra do drive, e o maior
 * deles é o próprio Remoto: `c:\Users\Particular\Documents\APPs\Remoto` (40
 * sessões) e `C:\Users\Particular\Documents\APPs\Remoto` (23) são a MESMA
 * pasta. Liberar "Remoto" sem normalizar cobriria 40 das 63 conversas e o
 * resto sumiria sem explicação nenhuma — o buraco silencioso que esta função
 * existe para não deixar acontecer.
 *
 * Só que normalizar caixa **sempre** quebraria o outro lado da frota: a
 * `vps-omni` é Debian, e ali o caminho DIFERENCIA maiúscula — `/opt/Remoto` e
 * `/opt/remoto` são duas pastas de verdade, e colapsá-las daria a uma pessoa
 * acesso a uma pasta que ninguém liberou. No Linux `\` ainda é um caractere
 * VÁLIDO de nome de arquivo, então trocar `\` por `/` lá também inventaria
 * igualdade onde não há.
 *
 * ## Por que o eixo é `papel`, e não `Maquina.so`
 *
 * `so` seria a verdade do sistema de arquivos, e foi recusado por duas razões:
 * ele é **anulável** (máquina que nunca pulsou não tem) e é **reescrito a cada
 * pulso** do daemon. Uma chave de normalização que muda de valor depois de o
 * grant estar gravado órfãna TODOS os grants daquela máquina de uma vez, e em
 * silêncio — ninguém recebe erro, as conversas só somem. `papel` é enum
 * (`PC`/`VPS`), nasce na aprovação da máquina e não anda sozinho.
 *
 * Um PC macOS cairia em `PC` e seria tratado como insensível à caixa: é o
 * comportamento correto para o padrão do APFS/HFS+, então o eixo acerta os
 * dois casos que a frota pode ter.
 *
 * ## A SEGUNDA escrita desta regra é SQL, e a paridade é provada
 *
 * O banco precisa da mesma normalização para manter `sessoes.cwd_projeto`
 * (o gatilho `sessoes_cwd_projeto` chama `remoto_cwd_do_projeto`), pelo mesmo
 * motivo de `remoto_texto_do_evento`: quem decide em massa é o Postgres. Duas
 * escritas da mesma regra é uma que envelhece calada — por isso as duas leem
 * as MESMAS fixturas (`fixturas-cwd.json`), e há teste dos dois lados que
 * morde se o JSON andar sozinho. A prova de migration roda as fixturas contra
 * a função de VERDADE do banco, não contra uma cópia dela.
 *
 * ## A caixa cai só no ASCII, e isso é decisão, não esquecimento
 *
 * `toLowerCase()` do JavaScript dobra acentuada (`Ç`→`ç`) SEMPRE. O `lower()`
 * do Postgres dobra ou não conforme a **collation** do banco: sob `C` ele só
 * mexe em A–Z. Como a collation da produção não é algo que este código possa
 * afirmar, dobrar tudo seria assinar uma paridade que depende de uma
 * configuração que ninguém conferiu — e paridade quebrada aqui não dá erro:
 * dá grant que deixa de casar e conversa que some da lista.
 *
 * Então as duas escritas dobram **só A–Z**, de propósito. O preço é honesto e
 * estreito: duas pastas que diferem apenas pela caixa de uma letra ACENTUADA
 * continuariam sendo dois projetos no Windows. Nunca aconteceu na frota (os 45
 * pares medidos são ASCII), e o dia em que acontecer aparece como projeto
 * repetido na lista — visível, que é o oposto do buraco que o ADR-0020 fecha.
 */

/**
 * O cwd reduzido à identidade do PROJETO.
 *
 * - `VPS` (Linux): só apara barra final. Caixa e `\` ficam INTOCADOS.
 * - `PC` (Windows/macOS): `\` vira `/`, apara barra final, e A–Z viram a–z.
 *
 * Barras repetidas **não** são colapsadas de propósito: `\\servidor\pasta`
 * (UNC) viraria `/servidor/pasta` e passaria a colidir com um caminho local
 * de mesmo nome.
 */
export function normalizarCwdDoProjeto(cwd: string, papel: PapelDaMaquina): string {
  const cru = cwd.trim();
  if (papel === 'VPS') return aparar(cru) || '/';
  return minusculaAscii(aparar(cru.replace(/\\/g, '/')) || '/');
}

/**
 * `lower()` só de A–Z — o mesmo recorte que a função SQL irmã faz com
 * `translate()`. Ver o bloco "A caixa cai só no ASCII" acima: é o único jeito
 * de a paridade não depender da collation do banco.
 */
function minusculaAscii(texto: string): string {
  return texto.replace(/[A-Z]/g, (letra) => letra.toLowerCase());
}

/** Apara barra(s) do fim sem comer a raiz (`/` sozinha vira string vazia). */
function aparar(caminho: string): string {
  return caminho.replace(/\/+$/, '');
}

/** A identidade de um projeto, como ela viaja no fio e mora no grant. */
export const ProjetoChaveSchema = z.object({
  maquinaId: z.string().min(1),
  /** Já normalizado pelo SERVIDOR — o cliente nunca decide esta string. */
  cwdProjeto: z.string().min(1).max(1024),
});
export type ProjetoChave = z.infer<typeof ProjetoChaveSchema>;

/** Um projeto como a tela de contas o lista, com o que ajuda a reconhecê-lo. */
export const ProjetoSchema = ProjetoChaveSchema.extend({
  maquinaNome: z.string(),
  /** Um dos cwd crus deste projeto, para a pessoa reconhecer a pasta. */
  cwdExibicao: z.string(),
  /** Quantas sessões já existiram nesta pasta — o peso da escolha. */
  sessoes: z.number().int().nonnegative(),
});
export type Projeto = z.infer<typeof ProjetoSchema>;
